/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import type { ReactNode } from 'react'

import { DateTime } from 'luxon'
import { FiMap } from 'react-icons/fi'
import { LuRows2, LuTable2 } from 'react-icons/lu'

import { useNavMode } from '@/components/Layout/NavModeContext'
import AppTitleBar from '@/components/ui/page-header/AppTitleBar'

export type ListLayoutMode = 'map' | 'split' | 'table'

// currentColor, so it shows on the white toolbar and the blue title bar alike.
export const ToolbarDivider = () => <div className="w-px h-6 bg-current/20 shrink-0" />

/** The count line and, once loaded, when the data was last refreshed (with a spinner while it refreshes). */
export const ListCounts = ({
  count,
  updatedAt,
  busy,
  formatTime = (date) => DateTime.fromJSDate(date).toFormat('yyyy-MM-dd HH:mm:ss'),
}: {
  count: ReactNode
  updatedAt?: Date
  busy?: boolean
  formatTime?: (date: Date) => string
}) => (
  // Inherits the bar's text colour, so it reads on the white toolbar and the blue title bar.
  <div className="flex flex-col shrink-0 opacity-60 text-xs">
    <div>{count}</div>
    {updatedAt && (
      <div className="flex items-center gap-1" data-testid="list-updated">
        Last updated: {formatTime(updatedAt)}
        {busy && <span className="loading loading-spinner loading-xs" aria-label="Refreshing" />}
      </div>
    )}
  </div>
)

/** Map, split (map over list) and table-only. */
export const LayoutSwitch = ({
  mode,
  onChange,
}: {
  mode: ListLayoutMode
  onChange: (mode: ListLayoutMode) => void
}) => (
  <div className="join shrink-0">
    {(
      [
        { value: 'map', icon: FiMap, title: 'Map view' },
        { value: 'split', icon: LuRows2, title: 'Split view' },
        { value: 'table', icon: LuTable2, title: 'Table view' },
      ] as const
    ).map(({ value, icon: Icon, title }) => (
      <button
        key={value}
        onClick={() => onChange(value)}
        title={title}
        aria-pressed={mode === value}
        className={`join-item btn btn-sm ${mode === value ? 'btn-primary' : 'btn-ghost border border-base-300'}`}
      >
        <Icon className="w-4 h-4" />
      </button>
    ))}
  </div>
)

export interface ListPageToolbarProps {
  /** The page's name, shown in the blue title bar when the nav is collapsed. */
  title: string
  savedViews: ReactNode
  search: ReactNode
  counts: ReactNode
  /** Company (and anything that follows it), pushed to the right of the top row. */
  scope: ReactNode
  /** Limit to map, download, configure. */
  listControls: ReactNode
  /** The map / split / table switch, where the page has one. */
  layoutSwitch?: ReactNode
  filters?: ReactNode
  /** Page actions at the right end of the second row. */
  actions?: ReactNode
  /** The detail sheet is open: the second row is put away. */
  sheetIsOpen: boolean
}

/**
 * The two rows at the top of a list page.
 *
 * The top row is the same in every state: saved views, search, counts, then the company on the
 * right. It sits in the page's blue title bar when the nav is collapsed, otherwise along the top.
 *
 * The second row holds the list controls, layout switch, filters and page actions, and goes away
 * while the detail sheet is open. Short of room it wraps upward, so the list controls end up on
 * the line right above the list.
 */
export const ListPageToolbar = ({
  title,
  savedViews,
  search,
  counts,
  scope,
  listControls,
  layoutSwitch,
  filters,
  actions,
  sheetIsOpen,
}: ListPageToolbarProps) => {
  const { isCollapsed } = useNavMode()

  const topRow = (
    <>
      {isCollapsed && <ToolbarDivider />}
      {savedViews}
      <ToolbarDivider />
      {search}
      {counts}
      <div className="ml-auto flex items-center gap-4 min-w-0">{scope}</div>
    </>
  )

  return (
    <>
      {isCollapsed ? (
        // With the sheet open, a small gap keeps the blue bar from running into the blue top of
        // the sheet (or the list's header) right below it.
        <div className={sheetIsOpen ? 'pb-1' : undefined}>
          <AppTitleBar title={title} align="start">
            <div className="flex flex-1 items-center gap-4 min-w-0">{topRow}</div>
          </AppTitleBar>
        </div>
      ) : (
        <div className="bg-base-100 px-4 py-2 relative z-40 flex items-center gap-4 border-b border-base-200">
          {topRow}
        </div>
      )}

      {!sheetIsOpen && (
        <div className="bg-base-100 px-4 py-2 relative z-40 flex flex-wrap-reverse items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-4 shrink-0">
            {listControls}
            <ToolbarDivider />
          </div>
          <div className="flex flex-1 flex-wrap items-center gap-x-4 gap-y-2">
            {layoutSwitch && (
              <>
                {layoutSwitch}
                <ToolbarDivider />
              </>
            )}
            {filters}
            {actions && <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-2">{actions}</div>}
          </div>
        </div>
      )}
    </>
  )
}
